/**
 * Canonical user preference contracts.
 *
 * Preferences that follow a person across browsers are persisted through the
 * canonical settings/preferences API. Report productivity preferences live in
 * this same versioned bag; scoped client/account/portfolio identities are never
 * valid saved-report parameters.
 */
export const PREFERENCE_SCHEMA_VERSION = 3;
export const DEFAULT_REPORTING_PREFERENCES = {
    favoriteReportIds: [],
    recentReportIds: [],
    presetsByReport: {},
};
export const DEFAULT_USER_PREFERENCES = {
    theme: 'system',
    language: 'en',
    dateFormat: 'DUAL',
    numberFormat: 'en-NP',
    timeZone: Intl?.DateTimeFormat()?.resolvedOptions?.()?.timeZone ?? 'Asia/Kathmandu',
    notifications: { email: true, inApp: true, push: false },
    defaultContext: undefined,
    defaultPageSize: 20,
    autoRefreshInterval: 0,
    reporting: DEFAULT_REPORTING_PREFERENCES,
    _schemaVersion: PREFERENCE_SCHEMA_VERSION,
};
export const DEFAULT_DEVICE_PREFERENCES = {
    sidebarCollapsed: false,
    themeOverride: undefined,
};
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isValidTheme(value) {
    return value === 'light' || value === 'dark' || value === 'system';
}
function isValidDateFormat(value) {
    return value === 'AD' || value === 'BS' || value === 'DUAL';
}
function isValidNumberFormat(value) {
    return value === 'en-NP' || value === 'en-US' || value === 'en-IN';
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
function stringArray(value, limit) {
    if (!Array.isArray(value))
        return [];
    return [...new Set(value
            .filter((item) => typeof item === 'string' && item.trim().length > 0)
            .map((item) => item.trim()))].slice(0, limit);
}
function safeSavedParameters(value) {
    if (!isRecord(value))
        return {};
    const entries = Object.entries(value)
        .filter(([field, item]) => {
        const normalized = field.toLowerCase();
        if (typeof item !== 'string' || item.length === 0)
            return false;
        if (normalized === 'page' || normalized === 'pagesize')
            return false;
        if (normalized.includes('client') || normalized.includes('portfolio'))
            return false;
        if (normalized.includes('account') || normalized.endsWith('id'))
            return false;
        return true;
    })
        .slice(0, 24);
    return Object.fromEntries(entries);
}
function reportingPreferences(value) {
    if (!isRecord(value))
        return { ...DEFAULT_REPORTING_PREFERENCES };
    const rawPresets = isRecord(value.presetsByReport) ? value.presetsByReport : {};
    const presetsByReport = Object.fromEntries(Object.entries(rawPresets)
        .slice(0, 64)
        .map(([reportId, rawViews]) => {
        const views = Array.isArray(rawViews)
            ? rawViews
                .filter(isRecord)
                .map((view) => ({
                id: typeof view.id === 'string' ? view.id : '',
                name: typeof view.name === 'string' ? view.name.trim().slice(0, 80) : '',
                parameters: safeSavedParameters(view.parameters),
                createdAt: typeof view.createdAt === 'string' ? view.createdAt : '',
            }))
                .filter((view) => view.id && view.name && view.createdAt)
                .slice(0, 8)
            : [];
        return [reportId, views];
    }));
    return {
        favoriteReportIds: stringArray(value.favoriteReportIds, 64),
        recentReportIds: stringArray(value.recentReportIds, 6),
        presetsByReport,
    };
}
/**
 * Convert a canonical UserPreferences object to the server storage shape.
 * The `locale` record is versioned so migrations are possible.
 */
export function toServerPreferencePayload(prefs) {
    const { theme, ...localeFields } = prefs;
    return {
        locale: { ...localeFields, _schemaVersion: PREFERENCE_SCHEMA_VERSION },
        theme,
    };
}
/**
 * Coerce a raw server payload (or any object) into a validated UserPreferences
 * instance. Unrecognized fields are ignored; missing fields fall back to defaults.
 */
export function fromServerPreferencePayload(raw) {
    if (!isRecord(raw)) {
        return { ...DEFAULT_USER_PREFERENCES, reporting: { ...DEFAULT_REPORTING_PREFERENCES } };
    }
    const locale = isRecord(raw.locale) ? raw.locale : {};
    const theme = isValidTheme(raw.theme) ? raw.theme : DEFAULT_USER_PREFERENCES.theme;
    const notificationsRaw = isRecord(locale.notifications) ? locale.notifications : {};
    const notifications = {
        email: notificationsRaw.email === true,
        inApp: notificationsRaw.inApp === true,
        push: notificationsRaw.push === true,
    };
    const defaultPageSizeRaw = Number(locale.defaultPageSize ?? DEFAULT_USER_PREFERENCES.defaultPageSize);
    const defaultPageSize = Number.isFinite(defaultPageSizeRaw)
        ? clamp(defaultPageSizeRaw, 5, 100)
        : DEFAULT_USER_PREFERENCES.defaultPageSize;
    const autoRefreshIntervalRaw = Number(locale.autoRefreshInterval ?? DEFAULT_USER_PREFERENCES.autoRefreshInterval);
    const autoRefreshInterval = Number.isFinite(autoRefreshIntervalRaw)
        ? clamp(autoRefreshIntervalRaw, 0, 3_600_000)
        : DEFAULT_USER_PREFERENCES.autoRefreshInterval;
    return {
        theme,
        language: typeof locale.language === 'string' && locale.language.length ? locale.language : DEFAULT_USER_PREFERENCES.language,
        dateFormat: isValidDateFormat(locale.dateFormat) ? locale.dateFormat : DEFAULT_USER_PREFERENCES.dateFormat,
        numberFormat: isValidNumberFormat(locale.numberFormat) ? locale.numberFormat : DEFAULT_USER_PREFERENCES.numberFormat,
        timeZone: typeof locale.timeZone === 'string' ? locale.timeZone : DEFAULT_USER_PREFERENCES.timeZone,
        notifications,
        defaultContext: typeof locale.defaultContext === 'string' ? locale.defaultContext : DEFAULT_USER_PREFERENCES.defaultContext,
        defaultPageSize,
        autoRefreshInterval,
        reporting: reportingPreferences(locale.reporting),
        _schemaVersion: PREFERENCE_SCHEMA_VERSION,
    };
}
/** Merge an existing stored preference object with the canonical default set. */
export function migratePreferences(raw) {
    const parsed = fromServerPreferencePayload(raw);
    return {
        ...DEFAULT_USER_PREFERENCES,
        ...parsed,
        reporting: parsed.reporting,
        _schemaVersion: PREFERENCE_SCHEMA_VERSION,
    };
}
/** Type guard and validator. Returns the validated preferences plus a flag. */
export function validateUserPreferences(raw) {
    const parsed = fromServerPreferencePayload(raw);
    const isValid = isRecord(raw) && raw.locale !== undefined && parsed._schemaVersion === PREFERENCE_SCHEMA_VERSION;
    return isValid ? { valid: true, preferences: parsed } : { valid: false, fallback: parsed };
}
/** Device-local storage key for the currently authenticated user. */
export function getDevicePreferencesKey(userId) {
    return `finance:device-preferences:${userId}`;
}
